import { useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { orderQueryOptions } from '@/api/resources/orders'
import { LoadingSpinner } from '@/components/feedback/loading-spinner'
import { OrderDetailHeader } from '@/components/orders/order-detail-header'
import { OrderLinesTable } from '@/components/orders/order-lines-table'
import { OrderShipmentsList } from '@/components/orders/order-shipments-list'

export const Route = createFileRoute('/_app/orders/$orderId')({
  head: ({ params }) => ({ meta: [{ title: `Order #${params.orderId} - MyJunea` }] }),
  beforeLoad: async ({ params }) => {
    if (Number.isNaN(Number(params.orderId))) {
      throw redirect({ to: '/orders', search: { page: 1 } })
    }
  },
  loader: ({ context, params }) => {
    void context.queryClient.ensureQueryData(orderQueryOptions(Number(params.orderId)))
  },
  component: OrderDetailPage,
})

function OrderDetailPage(): React.ReactElement {
  const { orderId } = Route.useParams()
  const id = Number(orderId)

  const { data: order, isLoading } = useQuery(orderQueryOptions(id))

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (order === undefined) {
    return <p className="text-muted-foreground py-10 text-center">Order not found.</p>
  }

  return (
    <div className="space-y-6">
      <OrderDetailHeader
        status={order.status}
        statusLabel={order.status_label}
        type={order.type}
        createdAt={order.created_at}
        deliveredAt={order.delivered_at}
        locationName={order.location_name}
        deliveryAddress={order.delivery_address}
      />

      <OrderShipmentsList shipments={order.shipments} />

      <OrderLinesTable
        items={order.items.map((item) => ({
          id: item.id,
          description: item.description,
          productName: item.product_name,
          imageUrl: item.image_url,
          amount: item.amount,
        }))}
      />
    </div>
  )
}
